import { useEffect, useState } from "react";
import { Play, Pause } from "lucide-react";

const BAR_HEIGHTS = [10, 18, 26, 14, 30, 20, 12, 24, 32, 16, 22, 28, 12, 20, 26, 14];

const formatTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function Waveform({ playing, light = false }) {
  return (
    <div className={`flex h-8 items-center gap-[3px] ${playing ? "" : "wave-paused"}`} aria-hidden="true">
      {BAR_HEIGHTS.map((h, i) => (
        <span
          key={i}
          className={`wave-bar w-[3px] rounded-full ${light ? "bg-indigo-300" : "bg-indigo-500"}`}
          style={{ height: `${h}px`, animationDelay: `${i * 0.08}s` }}
        />
      ))}
    </div>
  );
}

export default function AudioSampleCard({ title, duration = 42, testid }) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setElapsed((e) => {
        if (e + 0.25 >= duration) {
          setPlaying(false);
          return 0;
        }
        return e + 0.25;
      });
    }, 250);
    return () => clearInterval(id);
  }, [playing, duration]);

  const toggle = () => setPlaying((p) => !p);
  const progress = Math.min((elapsed / duration) * 100, 100);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          data-testid={`${testid}-toggle`}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-400">
            <span data-testid={`${testid}-elapsed`}>{formatTime(elapsed)}</span>
            {" / "}
            {formatTime(duration)}
          </p>
        </div>
        <Waveform playing={playing} />
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-indigo-600 transition-[width] duration-300 ease-linear"
          style={{ width: `${progress}%` }}
          data-testid={`${testid}-progress`}
        />
      </div>
    </div>
  );
}
